"use client";

import { socialMediaLinks } from "@/lib/constants/socialMediaLinks";
import React, { useRef, useState, useLayoutEffect } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { TextBlockContent } from "./Text";
import type { ButtonConfig, ThemeVariant } from "@/types";
import { HeadingTag, HeadingStyle } from "../ui/Heading";
import { NewsCard } from "@/components/ui/cards/newsCard";
import type { IconName } from "@/types/icons";
import type { ImageProps } from "@/types/image";
import { motion, useScroll, useTransform } from "motion/react";
import { IconButton } from "../ui/buttons";

const hotTopicsVariants = cva("container py-2xl", {
  variants: {
    variant: {
      light: "text-white",
      dark: "text-black",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface HotTopicTeaser {
  image?: ImageProps;
  icon?: IconName;
  headline: string;
  label: string;
  href?: string;
  target?: "_blank" | "_self";
}

export interface HotTopicsProps {
  // Text block props
  variant?: ThemeVariant;
  topline?: string;
  headline?: string;
  text?: string;
  headingTag?: HeadingTag;
  headingStyle?: HeadingStyle;
  button?: ButtonConfig;
  socialMedia?: boolean;

  topics: HotTopicTeaser[];
  className?: string;
}

/**
 * HotTopics component - specialized teaser layout with scrolling news cards
 * Similar to GalleryTeaser but uses NewsCards instead of simple images.
 */
export const HotTopics: React.FC<HotTopicsProps> = ({
  topline,
  headline,
  text,
  headingTag = "h2",
  headingStyle = "h2",
  button,
  variant = "dark",
  topics,
  className = "",
  socialMedia = false,
}) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [dynamicHeight, setDynamicHeight] = useState<string>("150vh");
  const [columnHeights, setColumnHeights] = useState({ left: 0, right: 0 });

  // Track the scroll progress of the entire section relative to the viewport
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"],
  });

  // Parallax: Differing start/end points for each column to create depth
  const y1 = useTransform(
    scrollYProgress,
    [0, 1],
    [
      0,
      -(typeof window !== "undefined"
        ? Math.max(0, columnHeights.left - window.innerHeight * 0.65)
        : 0),
    ]
  );
  const y2 = useTransform(
    scrollYProgress,
    [0, 1],
    [
      0,
      -(typeof window !== "undefined"
        ? Math.max(0, columnHeights.right - window.innerHeight * 0.8)
        : 0),
    ]
  );

  useLayoutEffect(() => {
    const calculateHeight = () => {
      if (!leftColumnRef.current || !rightColumnRef.current) return;

      const leftHeight = leftColumnRef.current.offsetHeight;
      const rightHeight = rightColumnRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;

      setColumnHeights({ left: leftHeight, right: rightHeight });

      // The dynamic height should be:
      // ViewportHeight + Max(Distance each column needs to travel to show its bottom)

      const travel1 = Math.max(0, leftHeight - viewportHeight * 0.8);
      const travel2 = Math.max(0, rightHeight - viewportHeight * 0.8);
      const maxTravel = Math.max(travel1, travel2);

      // Slower scroll: Increase the dynamic height factor
      // By multiplying maxTravel by a factor (e.g. 1.3), the same travel distance
      // is spread across a longer scroll distance.
      const scrollSlowdownFactor = 1.3;
      setDynamicHeight(
        `${viewportHeight + maxTravel * scrollSlowdownFactor + 100}px`
      );
    };

    calculateHeight();
    const observer = new ResizeObserver(calculateHeight);
    if (leftColumnRef.current) observer.observe(leftColumnRef.current);
    if (rightColumnRef.current) observer.observe(rightColumnRef.current);

    window.addEventListener("resize", calculateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", calculateHeight);
    };
  }, [topics.length]);

  // Split cards into two columns
  const column1Topics = topics.filter((_, index) => index % 2 === 0);
  const column2Topics = topics.filter((_, index) => index % 2 === 1);

  return (
    <div
      ref={targetRef}
      className="relative lg:h-(--dynamic-height)"
      // @ts-expect-error - dynamic variable for CSS
      style={{ "--dynamic-height": dynamicHeight }}
    >
      <div
        className={hotTopicsVariants({
          variant,
          className: `${className} lg:sticky top-0 h-auto lg:h-screen flex items-center overflow-visible lg:overflow-hidden`,
        })}
      >
        <div className="col-span-full lg:col-span-12 relative grid grid-cols-12 gap-md w-full py-xl lg:py-0">
          <div className="col-span-full lg:col-span-5 flex flex-col justify-center mb-xl lg:mb-0">
            <TextBlockContent
              topline={topline}
              headline={headline}
              text={text}
              headingTag={headingTag}
              headingStyle={headingStyle}
              button={button}
              variant={variant}
            />
            {socialMedia && (
              <div className="flex gap-4 mt-md">
                {socialMediaLinks.map((link) => (
                  <IconButton
                    key={link.name}
                    href={link.url}
                    icon={link.icon}
                    label={link.name}
                    color={variant === "light" ? "yellow" : "purple"}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ))}
              </div>
            )}
          </div>

          <div className="col-span-full lg:col-start-7 lg:col-span-6 relative flex items-center h-auto lg:h-screen">
            <div className="flex flex-col md:grid md:grid-cols-2 gap-md h-full w-full">
              {/* Left Column (even indices) */}
              <motion.div
                ref={leftColumnRef}
                className="flex flex-col justify-start gap-md lg:pt-32 h-fit"
                initial={false}
                style={{
                  y:
                    typeof window !== "undefined" && window.innerWidth >= 1024
                      ? y1
                      : 0,
                }}
              >
                {column1Topics.map((topic, index) => {
                  // Pattern for left: top, bottom, top, bottom...
                  const align = index % 2 === 0 ? "top" : "bottom";
                  return (
                    <NewsCard
                      key={`col1-${index}`}
                      {...topic}
                      align="bottom"
                      aspect="2/3"
                      padding="lg"
                    />
                  );
                })}
              </motion.div>

              {/* Right Column (odd indices) */}
              <motion.div
                ref={rightColumnRef}
                className="flex flex-col justify-start gap-md h-fit"
                initial={false}
                style={{
                  y:
                    typeof window !== "undefined" && window.innerWidth >= 1024
                      ? y2
                      : 0,
                }}
              >
                {column2Topics.map((topic, index) => {
                  // Pattern for right: bottom, top, bottom, top...
                  const align = index % 2 === 0 ? "bottom" : "top";
                  return (
                    <NewsCard
                      key={`col2-${index}`}
                      {...topic}
                      align="bottom"
                      aspect="2/3"
                      padding="lg"
                    />
                  );
                })}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
